import React, { useCallback } from 'react';

import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';

import { getMembership } from '@techstyle/react-accounts';
import { FormattedMessage } from '@techstyle/react-intl';

import {
  deleteBasketItem,
  deleteBasketItemsByProductId,
  deleteProductSet,
} from '../../actions/checkout';
import { MembershipActionLocation } from '../../constants/checkout';
import { useUpdateCartDrawerItemQuantityMutation } from '../../services/cartApi';
import { useLDNewShoppingBagUX } from '../../utils/LD/useNewShoppingBagUX';
import {
  allowExclusiveCheckout as allowExclusiveCheckoutSelector,
  getLineItems,
  getMembershipCartItem,
  hasNonPreorderItems as hasNonPreorderItemsSelector,
  onlyHasPreorderItems as onlyHasPreorderItemsSelector,
} from '../../utils/selectors';
import { Container as BasketItemList } from '../BasketItemList';

import { useAddMembershipFromCartDrawer } from './useAddMembershipFromCartDrawer';
import { useMemberFreeShipping } from './useFreeShipping';
import { useRemoveMembershipFromCartDrawer } from './useRemoveMembershipFromCartDrawer';
import { useToastMessage } from './useToastMessage';
import { useUpsellAssets } from './useUpsellProducts';

export function useBasketListPropsAdapter() {
  const hasNonPreorderItems = useSelector(hasNonPreorderItemsSelector);
  const isBorderfreeCustomer = useSelector(
    ({ borderfree }: RootStateOrAny) => borderfree.isBorderfreeCustomer
  );

  const { cartLineDiscounts: lineDiscounts } = useSelector(
    ({ checkout }: RootStateOrAny) => checkout
  );

  const allowExclusiveCheckout = useSelector(allowExclusiveCheckoutSelector);

  const hasMemberFreeShipping = useMemberFreeShipping();
  const isTopItem = hasMemberFreeShipping || allowExclusiveCheckout;
  const isInfluencer = useSelector((state: RootStateOrAny) =>
    Boolean(state.customer.isInfluencer)
  );
  const isVip = useSelector(
    (state: RootStateOrAny) => getMembership(state).isVip
  );
  const location = MembershipActionLocation.MINI_CART;
  const membershipItem = useSelector(getMembershipCartItem);
  const onlyHasPreorderItems = useSelector(onlyHasPreorderItemsSelector);
  const products = useSelector(getLineItems);

  const showPrice = false;
  const showQuantitySelector = false;
  const showQuantity = false;
  const showRemoveIcon = true;

  return {
    pSource: 'mini_cart',
    lineDiscounts,
    hasNonPreorderItems,
    isBorderfreeCustomer,
    isTopItem,
    isVip,
    isInfluencer,
    location,
    membershipItem,
    onlyHasPreorderItems,
    products,
    showPrice,
    showQuantitySelector,
    showQuantity,
    showRemoveIcon,
  };
}

export function useUpdateItemQuantity() {
  const [updateCartDrawerItemQuantity] =
    useUpdateCartDrawerItemQuantityMutation();
  const updateItemQuantityHandler = useCallback(
    (productGroupKey, productType, quantity) => {
      if (productType) {
        updateCartDrawerItemQuantity({
          productGroupKey,
          quantity,
          kind: 'productSet',
        });
      } else {
        updateCartDrawerItemQuantity({
          productGroupKey,
          quantity,
          kind: 'productById',
        });
      }
    },
    [updateCartDrawerItemQuantity]
  );
  return updateItemQuantityHandler;
}

const useControlHandler = () => {
  const dispatch = useDispatch();
  return useCallback(
    ({ isEchoCart, productGroupKey, productType, shouldDeleteByProductId }) => {
      if (Boolean(productType) && !isEchoCart) {
        dispatch(
          deleteProductSet(
            productGroupKey,
            MembershipActionLocation.MINI_CART,
            false
          )
        );
      } else if (shouldDeleteByProductId) {
        dispatch(
          deleteBasketItemsByProductId(
            productGroupKey,
            MembershipActionLocation.MINI_CART
          )
        );
      } else {
        dispatch(
          deleteBasketItem(
            productGroupKey,
            MembershipActionLocation.MINI_CART,
            false,
            undefined
          )
        );
      }
    },
    [dispatch]
  );
};

export function useBasketItemDeleteHandler() {
  const { showToastMessage } = useToastMessage();
  const controlHandler = useControlHandler();

  const showMessage = useCallback(
    shouldDeleteByProductId => {
      const toastMessage = shouldDeleteByProductId ? (
        <FormattedMessage
          id="global_checkout.bundled_items_removed_message"
          defaultMessage="Bundled items removed"
        />
      ) : (
        <FormattedMessage
          id="global_checkout.cart_item_removed_message"
          defaultMessage="Item removed"
        />
      );

      showToastMessage(toastMessage);
    },
    [showToastMessage]
  );
  return useCallback(
    ({ isEchoCart, productGroupKey, productType, shouldDeleteByProductId }) => {
      showMessage(shouldDeleteByProductId);
      return controlHandler({
        isEchoCart,
        productGroupKey,
        productType,
        shouldDeleteByProductId,
      });
    },
    [controlHandler, showMessage]
  );
}

export function CartDrawerBasketItemList() {
  const basketListProps = useBasketListPropsAdapter();
  const handleDeleteBasketItem = useBasketItemDeleteHandler();
  const updateItemQuantity = useUpdateItemQuantity();
  const removeMembership = useRemoveMembershipFromCartDrawer();
  const addMembership = useAddMembershipFromCartDrawer();
  const { upsellAssetToTrack, upsellProduct } = useUpsellAssets();
  const { isControl } = useLDNewShoppingBagUX();

  return (
    <BasketItemList
      {...basketListProps}
      deleteBasketItem={handleDeleteBasketItem}
      updateItemQuantity={updateItemQuantity}
      removeMembership={removeMembership}
      addMembership={addMembership}
      upsellAssetToTrack={upsellAssetToTrack}
      upsellProduct={upsellProduct}
      isRevampedShoppingBag={!isControl}
    />
  );
}
